import React from 'react';
import { Box, FormHelperText, Grid, InputLabel, ListItemIcon, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import { SwitchField } from '../../../components/form-drawer';
import { ACESSO_RAPIDO_ICONS } from '../../../model/apm/apm-enums';
import { FieldErrors, FieldMap, FieldSpec } from './field-config';
import { RichTextEditor } from './rich-text-editor';
import { ImageUploadField } from './image-upload-field';
import { DocumentUploadField } from './document-upload-field';
import { AcessoRapidoIcon } from './apm-icons';

interface ConfiguredFieldsProps<K extends string> {
    idPrefix: string;
    fields: FieldMap<K>;
    value: Record<string, any>;
    errors: FieldErrors;
    showErrors: boolean;
    onChange: (patch: Record<string, any>) => void;
    onUploadingChange?: (uploading: boolean) => void;
}

function FieldError({ message }: { message?: string }) {
    return message ? <FormHelperText error>{message}</FormHelperText> : null;
}

function renderField(key: string, spec: FieldSpec, props: ConfiguredFieldsProps<string>) {
    const { idPrefix, value, errors, showErrors, onChange, onUploadingChange } = props;
    const error = showErrors ? errors[key] : undefined;
    const label = <InputLabel required={spec.required}>{spec.label}</InputLabel>;
    const helper = !error && spec.helper ? <FormHelperText>{spec.helper}</FormHelperText> : null;

    switch (spec.kind) {
        case 'richText':
            return (
                <Grid item xs={12} key={key}>
                    {label}
                    <RichTextEditor value={value[key]} onChange={(html) => onChange({ [key]: html })} error={!!error} />
                    <FieldError message={error} />
                </Grid>
            );
        case 'image':
            return (
                <Grid item xs={12} key={key}>
                    {label}
                    <ImageUploadField
                        id={`${idPrefix}-${key}`}
                        value={value[key]}
                        onChange={(fileName) => onChange({ [key]: fileName })}
                        onUploadingChange={onUploadingChange}
                        error={!!error}
                        helperText={error}
                        clearable={!spec.required}
                    />
                </Grid>
            );
        case 'file':
            return (
                <Grid item xs={12} key={key}>
                    {label}
                    <DocumentUploadField
                        id={`${idPrefix}-${key}`}
                        value={{ file: value[key], originalName: value[`${key}OriginalName`], size: value[`${key}Size`] }}
                        onChange={(doc) => onChange({ [key]: doc.file, [`${key}OriginalName`]: doc.originalName, [`${key}Size`]: doc.size })}
                        onUploadingChange={onUploadingChange}
                        error={!!error}
                        helperText={error}
                        clearable={!spec.required}
                    />
                </Grid>
            );
        case 'icon':
            return (
                <Grid item xs={12} md={6} key={key}>
                    {label}
                    <Select
                        fullWidth
                        value={value[key] || ''}
                        onChange={(e) => onChange({ [key]: e.target.value || undefined })}
                        error={!!error}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AcessoRapidoIcon name={selected as string} />
                                {ACESSO_RAPIDO_ICONS.find((i) => i.value === selected)?.label || selected}
                            </Box>
                        )}
                    >
                        {ACESSO_RAPIDO_ICONS.map((icon) => (
                            <MenuItem key={icon.value} value={icon.value}>
                                <ListItemIcon><AcessoRapidoIcon name={icon.value} /></ListItemIcon>
                                <ListItemText primary={icon.label} />
                            </MenuItem>
                        ))}
                    </Select>
                    <FieldError message={error} />
                </Grid>
            );
        case 'checkbox':
            return (
                <Grid item xs={12} key={key}>
                    <SwitchField label={spec.label} description={spec.helper} checked={!!value[key]} onChange={(checked) => onChange({ [key]: checked })} />
                </Grid>
            );
        case 'date':
            // Período de exibição: grava startDate/endDate
            return (
                <Grid item xs={12} key={key}>
                    <InputLabel required={spec.required}>{spec.label}</InputLabel>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Início</InputLabel>
                            <OutlinedInput fullWidth type="date" value={value.startDate || ''} onChange={(e) => onChange({ startDate: e.target.value || undefined })} error={!!error} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Fim</InputLabel>
                            <OutlinedInput fullWidth type="date" value={value.endDate || ''} onChange={(e) => onChange({ endDate: e.target.value || undefined })} error={!!error} />
                        </Grid>
                    </Grid>
                    {error ? <FieldError message={error} /> : <FormHelperText>Deixe em branco para exibir sempre</FormHelperText>}
                </Grid>
            );
        case 'number':
            return (
                <Grid item xs={12} md={6} key={key}>
                    {label}
                    <OutlinedInput
                        fullWidth
                        type="number"
                        inputProps={{ min: spec.min, max: spec.max }}
                        value={value[key] ?? ''}
                        onChange={(e) => onChange({ [key]: e.target.value === '' ? undefined : Number(e.target.value) })}
                        error={!!error}
                    />
                    {error ? <FieldError message={error} /> : spec.min !== undefined && <FormHelperText>Entre {spec.min} e {spec.max}</FormHelperText>}
                </Grid>
            );
        default:
            return (
                <Grid item xs={12} md={spec.md ?? 12} key={key}>
                    {label}
                    <OutlinedInput
                        fullWidth
                        autoComplete="off"
                        multiline={spec.kind === 'multiline'}
                        minRows={spec.kind === 'multiline' ? 3 : undefined}
                        placeholder={spec.kind === 'url' || spec.kind === 'youtube' ? 'https://' : undefined}
                        value={value[key] || ''}
                        onChange={(e) => onChange({ [key]: e.target.value })}
                        error={!!error}
                    />
                    <FieldError message={error} />
                    {helper}
                </Grid>
            );
    }
}

export function ConfiguredFields<K extends string>(props: ConfiguredFieldsProps<K>) {
    const entries = Object.entries(props.fields) as [string, FieldSpec][];
    return <React.Fragment>{entries.map(([key, spec]) => renderField(key, spec, props as ConfiguredFieldsProps<string>))}</React.Fragment>;
}
